import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const StoreContext = createContext();

const CART_STORAGE_KEY = 'thetopia_cart';
const BASKET_STORAGE_KEY = 'thetopia_basket_ident';
const USERNAME_STORAGE_KEY = 'thetopia_minecraft_username';

// Helper to safely get from localStorage (works during SSR too)
const getFromStorage = (key, defaultValue = null) => {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const item = localStorage.getItem(key);
    return item !== null ? item : defaultValue;
  } catch {
    return defaultValue;
  }
};

const getCartFromStorage = () => {
  if (typeof window === 'undefined') return [];
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

export function StoreProvider({ children }) {
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  // Initialize from localStorage synchronously to prevent flash
  const [cart, setCart] = useState(getCartFromStorage);
  const [basketIdent, setBasketIdent] = useState(() => getFromStorage(BASKET_STORAGE_KEY));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [minecraftUsername, setMinecraftUsername] = useState(() => getFromStorage(USERNAME_STORAGE_KEY, ''));
  const [ownedPackageIds, setOwnedPackageIds] = useState([]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  // Save basket ident to localStorage
  useEffect(() => {
    if (basketIdent) {
      localStorage.setItem(BASKET_STORAGE_KEY, basketIdent);
    }
  }, [basketIdent]);

  // Save username to localStorage
  useEffect(() => {
    if (minecraftUsername) {
      localStorage.setItem(USERNAME_STORAGE_KEY, minecraftUsername);
    }
  }, [minecraftUsername]);

  // Fetch categories (only once)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesRes = await fetch('/api/tebex/categories');
        if (!categoriesRes.ok) throw new Error('Failed to fetch categories');
        const categoriesData = await categoriesRes.json();
        const cats = categoriesData.data || [];
        setCategories(cats);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch player's purchased packages when username changes
  useEffect(() => {
    const fetchPlayerPurchases = async () => {
      if (!minecraftUsername) {
        setOwnedPackageIds([]);
        return;
      }

      try {
        const response = await fetch(`/api/tebex/player-purchases?username=${encodeURIComponent(minecraftUsername)}`);
        if (response.ok) {
          const data = await response.json();
          setOwnedPackageIds(data.ownedPackageIds || []);
          // Update username with correct capitalization from Mojang
          if (data.correctUsername && data.correctUsername !== minecraftUsername) {
            setMinecraftUsername(data.correctUsername);
            localStorage.setItem(USERNAME_STORAGE_KEY, data.correctUsername);
          }
        }
      } catch (err) {
        console.error('Error fetching player purchases:', err);
        setOwnedPackageIds([]);
      }
    };

    fetchPlayerPurchases();
  }, [minecraftUsername]);

  // Create basket when username changes (needed for checkout)
  useEffect(() => {
    const createBasketForUser = async () => {
      if (!minecraftUsername) {
        setBasketIdent(null);
        localStorage.removeItem(BASKET_STORAGE_KEY);
        return;
      }

      try {
        const response = await fetch('/api/tebex/basket', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: minecraftUsername }),
        });

        if (response.ok) {
          const data = await response.json();
          const ident = data.data?.ident;
          if (ident) {
            setBasketIdent(ident);
          }
        }
      } catch (err) {
        console.error('Error creating basket for user:', err);
      }
    };

    createBasketForUser();
  }, [minecraftUsername]);

  // Fetch packages once on mount (basketIdent doesn't affect pricing from API)
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);

        const packagesRes = await fetch('/api/tebex/packages');
        if (!packagesRes.ok) throw new Error('Failed to fetch packages');

        const packagesData = await packagesRes.json();

        // Extract packages with category info
        const allPackages = (packagesData.data || []).map(pkg => ({
          ...pkg,
          categoryId: pkg.category?.id,
        }));

        setPackages(allPackages);
        setError(null);
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // Create a new basket with username
  const createBasket = useCallback(async (username) => {
    try {
      const response = await fetch('/api/tebex/basket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create basket');
      }

      const data = await response.json();
      const ident = data.data?.ident;
      if (ident) {
        setBasketIdent(ident);
        return ident;
      }
      throw new Error('No basket ident returned');
    } catch (err) {
      console.error('Error creating basket:', err);
      throw err;
    }
  }, []);

  // Add item to cart
  const addToCart = useCallback((pkg) => {
    setCart(prevCart => {
      const existing = prevCart.find(item => item.id === pkg.id);
      if (existing) {
        return prevCart.map(item =>
          item.id === pkg.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...pkg, quantity: 1 }];
    });
    setCartOpen(true);
  }, []);

  // Remove item from cart
  const removeFromCart = useCallback((packageId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== packageId));
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((packageId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(packageId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === packageId ? { ...item, quantity } : item
      )
    );
  }, [removeFromCart]);

  // Clear cart
  const clearCart = useCallback(() => {
    setCart([]);
    setBasketIdent(null);
    localStorage.removeItem(BASKET_STORAGE_KEY);
  }, []);

  // Calculate cart total
  const cartTotal = cart.reduce((total, item) => {
    const price = parseFloat(item.base_price) || 0;
    return total + (price * item.quantity);
  }, 0);

  // Cart item count
  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  // Checkout - sync cart to Tebex basket and get checkout URL
  const checkout = useCallback(async (username) => {
    try {
      // Create a fresh basket for checkout with username
      const ident = await createBasket(username);

      // Add all cart items to the basket
      for (const item of cart) {
        await fetch('/api/tebex/basket', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ident,
            package_id: item.id,
            quantity: item.quantity,
          }),
        });
      }

      // Fetch the updated basket to get checkout links
      const basketRes = await fetch(`/api/tebex/basket?ident=${ident}`);
      if (!basketRes.ok) throw new Error('Failed to fetch basket');

      const basketData = await basketRes.json();
      return basketData.data;
    } catch (err) {
      console.error('Checkout error:', err);
      throw err;
    }
  }, [cart, createBasket]);

  return (
    <StoreContext.Provider
      value={{
        categories,
        packages,
        cart,
        cartOpen,
        setCartOpen,
        loading,
        error,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartTotal,
        cartCount,
        checkout,
        basketIdent,
        minecraftUsername,
        setMinecraftUsername,
        ownedPackageIds,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}
